import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { permissionsApi } from '../services/api';

interface Permission {
    resource: string;
    action: string;
}

interface UserPermissions {
    rolePermissions: string[];
    userPermissions: Permission[];
    revokedPermissions: Permission[];
    effectivePermissions: string[];
}

export const usePermissions = () => {
    const { user } = useAuth();
    const [permissions, setPermissions] = useState<UserPermissions | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (!user) {
                setPermissions(null);
                setLoading(false);
                return;
            }

            try {
                const response = await permissionsApi.getMyPermissions();
                // console.log('✅ Fetched permissions successfully:', response.data.data);
                setPermissions(response.data.data);
            } catch (error) {
                console.error('❌ Failed to fetch permissions:', error);
                setPermissions(null);
            } finally {
                // console.log('🏁 Permissions loading finished, setting loading = false');
                setLoading(false);
            }
        };

        fetchPermissions();
    }, [user]);

    const hasPermission = (resource: string, action: string): boolean => {
        if (!user) return false;

        // Admin has all permissions
        if (user.role === 'ADMIN') return true;

        // If still loading or no permissions, use role-based fallback
        if (loading || !permissions) {
            // console.log(`Using fallback permission check for ${user.role}: ${resource}:${action}`);
            // Role-based permission fallback
            switch (user.role) {
                case 'MANAGER':
                    if (resource === 'dashboard') return true;
                    if (resource === 'campus' && ['read_all', 'read_own', 'read', 'update'].includes(action)) return true;
                    if (resource === 'class' && ['read_all', 'read', 'create', 'update'].includes(action)) return true;
                    if (resource === 'student' && ['read_all', 'read', 'create', 'update', 'delete'].includes(action)) return true;
                    if (resource === 'teacher' && ['read_all', 'read'].includes(action)) return true;
                    if (resource === 'attendance' && ['read_all', 'read', 'create', 'update', 'mark'].includes(action)) return true;
                    if (resource === 'user' && ['read_all', 'read'].includes(action)) return true;
                    break;
                case 'TEACHER':
                case 'ASSISTANT':
                    if (resource === 'dashboard') return true;
                    if (resource === 'campus' && ['read'].includes(action)) return true;
                    if (resource === 'class' && ['read_own', 'read'].includes(action)) return true;
                    if (resource === 'student' && ['read_own_classes', 'read'].includes(action)) return true;
                    if (resource === 'teacher' && ['read_all', 'read'].includes(action)) return true;
                    if (resource === 'attendance' && ['read_own', 'read', 'create', 'update', 'mark'].includes(action)) return true;
                    break;
                case 'STAFF':
                    if (resource === 'dashboard') return true;
                    if (resource === 'student' && ['read', 'update'].includes(action)) return true;
                    break;
            }
            return false;
        }

        const permissionString = `${resource}:${action}`;
        return permissions.effectivePermissions.includes(permissionString);
    };

    // Same role fallback as hasPermission while loading; a second, drifting copy made pages flash
    // "no access" to teachers until their permissions arrived.
    const hasAnyPermission = (permissionsList: Array<{ resource: string, action: string }>): boolean =>
        permissionsList.some(({ resource, action }) => hasPermission(resource, action));

    return {
        permissions,
        loading,
        hasPermission,
        hasAnyPermission
    };
};
